import { useLocalStorage } from "@rehooks/local-storage";
import { useCallback, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { SortStateDescriptor } from "components/Filters/types";
import {
  EmptystateHelmetColored,
  EmptystateSearchNoResultsColored,
} from "components/icons/generated";
import ListContentWrapper from "components/ListContentWrapper";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import SearchInput from "components/SearchInput";
import { getSearchQuery } from "components/SearchInput/helpers";
import TablePagination from "components/Table/Pagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";
import usePagination from "components/Table/Pagination/usePagination";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import Table from "ds/components/Table";
import TableBody from "ds/components/Table/Body";
import TableSkeleton from "ds/components/Table/Skeleton";
import TableWrapper from "ds/components/Table/Wrapper";
import TableSimpleHead from "ds/components/TableSimple/Head";
import useErrorHandle from "hooks/useErrorHandle";
import { SearchQueryOrder } from "types/generated";

import {
  SPACE_POLICIES_FILTERS_ORDER_KEY,
  SPACE_POLICIES_INITIAL_LIMIT,
  SPACE_POLICIES_INITIAL_ORDER_BY,
  SPACE_POLICIES_LIMIT_STORAGE_KEY,
  SPACE_POLICIES_SORT_CONFIG,
} from "./constants";
import SpaceDetailsPoliciesRows from "./Rows";
import useSpaceDetailsPolicies from "./useSpaceDetailsPolicies";
import useSpaceDetailsPoliciesTableStore from "./useStore";

type SpaceDetailsPoliciesProps = {
  spaceId: string;
};

const TABLE_ARIA_LABEL = "Space policies table";

const SpaceDetailsPolicies = ({ spaceId }: SpaceDetailsPoliciesProps) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const [orderBy, setOrderBy] = useState<SearchQueryOrder>(SPACE_POLICIES_INITIAL_ORDER_BY);
  const [storedRowsPerPage] = useLocalStorage<number>(
    SPACE_POLICIES_LIMIT_STORAGE_KEY,
    SPACE_POLICIES_INITIAL_LIMIT
  );

  const first = storedRowsPerPage || SPACE_POLICIES_INITIAL_LIMIT;

  const fullTextSearch = getSearchQuery(searchParams) || null;

  const columnIds = useSpaceDetailsPoliciesTableStore((state) => state.columnsOrder.visible);

  const {
    loadPolicies,
    error,
    policies,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor,
    startCursor,
    paginationResetKey,
  } = useSpaceDetailsPolicies({ spaceId, orderBy, first, fullTextSearch });

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: loadPolicies,
    endCursor,
    startCursor,
    allItemsCount: totalCount,
    visibleItemsCount: policies.length,
    initialLimit: SPACE_POLICIES_INITIAL_LIMIT,
    storageKey: SPACE_POLICIES_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const handleSortCallback = useCallback((descriptor: SortStateDescriptor) => {
    setOrderBy({ field: descriptor.sortKey, direction: descriptor.sortDirection });
  }, []);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return <PageLayoutSkeleton />;
  }

  return (
    <>
      <PageInfoHeaderWrapper>
        <PageInfoHeaderTitle title="Policies" />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder="Search by policy name"
            filtersOrderSettingsKey={SPACE_POLICIES_FILTERS_ORDER_KEY}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>

      <ListContentWrapper>
        {!policies.length && !fullTextSearch && (
          <EmptyState
            title="This space has no policies yet"
            icon={EmptystateHelmetColored}
            caption="Policies assigned to this space will show up here."
          />
        )}

        {!policies.length && !!fullTextSearch && (
          <EmptyState
            title="No policies found"
            icon={EmptystateSearchNoResultsColored}
            caption="No policies match your search."
          />
        )}

        {!!policies.length && (
          <>
            <TableWrapper ref={tableRef} hasPagination>
              <Table ariaLabel={TABLE_ARIA_LABEL}>
                <TableSimpleHead
                  store={useSpaceDetailsPoliciesTableStore}
                  ariaLabel={TABLE_ARIA_LABEL}
                  tableRef={tableRef}
                  sortCallback={handleSortCallback}
                  sortConfig={SPACE_POLICIES_SORT_CONFIG}
                  filtersStorageKey={SPACE_POLICIES_FILTERS_ORDER_KEY}
                  hideSettings
                />
                {loadingContent && (
                  <TableSkeleton columnIds={columnIds} rowsPerPage={rowsPerPage} />
                )}
                {!loadingContent && (
                  <TableBody>
                    <SpaceDetailsPoliciesRows
                      data={policies}
                      store={useSpaceDetailsPoliciesTableStore}
                    />
                  </TableBody>
                )}
              </Table>
            </TableWrapper>
            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={totalCount}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </ListContentWrapper>
    </>
  );
};

export default SpaceDetailsPolicies;
